import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Loader2, LogOut, Plus, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listProjects } from "@/api/projects";
import { useAuthStore } from "@/stores/auth.store";
import { useProjectStore } from "@/stores/project.store";
import { PrismMark } from "@/components/auth/PrismMark";
import type { Project } from "@/api/types";
import { cn } from "@/lib/utils";

type Status = "loading" | "ok" | "error";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const projects = useProjectStore((s) => s.projects);
  const setProjects = useProjectStore((s) => s.setProjects);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const [status, setStatus] = useState<Status>(
    projects.length === 0 ? "loading" : "ok",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setStatus("loading");
    setErrorMessage(null);
    try {
      const data = await listProjects();
      setProjects(data);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setStatus("error");
    }
  }

  function open(project: Project) {
    setCurrentProject(project);
    navigate(`/projects/${project.slug}/dashboard`);
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="relative min-h-screen bg-slate-50">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{
          background:
            "radial-gradient(circle at top, rgba(79,70,229,0.08) 0%, rgba(248,250,252,0) 60%)",
        }}
      />
      <div className="absolute right-6 top-6 z-10 flex items-center gap-3 text-sm">
        {user && (
          <span className="text-slate-500">
            <span className="font-medium text-slate-700">{user.full_name}</span>
            <span className="mx-2 text-slate-300">·</span>
            {user.email}
          </span>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>

      <main className="relative mx-auto flex max-w-[720px] flex-col items-center px-6 pb-20 pt-24">
        <PrismMark size={48} className="mb-8 text-indigo-500/60" />

        <header className="mb-10 text-center">
          <h1 className="mb-2 text-4xl font-semibold tracking-tight text-slate-900">
            Your projects
          </h1>
          <p className="text-base font-light text-slate-500">
            Select a workspace
          </p>
        </header>

        <div className="w-full">
          {status === "loading" && <ProjectsSkeleton />}
          {status === "error" && (
            <ErrorState message={errorMessage} onRetry={load} />
          )}
          {status === "ok" && projects.length === 0 && <EmptyState />}
          {status === "ok" && projects.length > 0 && (
            <div className="flex flex-col gap-4">
              {projects.map((p) => (
                <ProjectCard key={p.id} project={p} onClick={() => open(p)} />
              ))}
              <NewProjectCard />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function ProjectCard({
  project,
  onClick,
}: {
  project: Project;
  onClick: () => void;
}) {
  const initial = project.name.charAt(0).toUpperCase();
  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative flex w-full items-center overflow-hidden rounded-plane bg-white p-6 text-left shadow-plane",
        "transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-lift-strong motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        "before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-indigo-500 before:opacity-0 before:transition-opacity hover:before:opacity-100",
      )}
    >
      <div className="mr-5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-sm font-semibold text-white shadow-sm">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <h2 className="truncate text-lg font-medium text-slate-900">
            {project.name}
          </h2>
          {project.partner_name && (
            <span className="truncate text-sm font-light text-slate-500">
              {project.partner_name}
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[13px] font-light text-slate-500">
          <span>{project.project_type.name}</span>
          <span className="opacity-50">·</span>
          <span className="capitalize">{project.project_type.engine_type}</span>
          {!project.is_active && (
            <>
              <span className="opacity-50">·</span>
              <span className="text-amber-600">Inactive</span>
            </>
          )}
        </div>
      </div>
      <ChevronRight className="ml-4 h-5 w-5 shrink-0 text-slate-300 transition-all duration-300 group-hover:text-indigo-500" />
    </button>
  );
}

function NewProjectCard() {
  return (
    <button
      type="button"
      disabled
      className="flex w-full cursor-not-allowed items-center rounded-plane border-2 border-dashed border-slate-200 bg-transparent p-6 text-left opacity-60"
      title="Coming soon"
    >
      <div className="mr-5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-slate-400">
        <Plus className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <h2 className="text-lg font-medium text-slate-700">New project</h2>
        <p className="text-sm font-light text-slate-500">
          Create a workspace
        </p>
      </div>
    </button>
  );
}

function ProjectsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="flex items-center rounded-plane bg-white p-6 shadow-plane"
        >
          <div className="mr-5 h-10 w-10 animate-pulse rounded-full bg-slate-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ))}
      <div className="flex items-center justify-center gap-2 pt-2 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading projects…
      </div>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-plane border border-rose-200 bg-rose-50 p-6 text-center">
      <p className="text-sm font-medium text-rose-700">
        Couldn&rsquo;t load your projects
      </p>
      {message && <p className="mt-1 text-xs text-rose-600">{message}</p>}
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="mt-3 border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-plane bg-white p-10 text-center shadow-plane">
      <p className="text-base font-medium text-slate-700">
        No projects yet
      </p>
      <p className="mt-1 text-sm font-light text-slate-500">
        Ask your administrator to add you to a workspace.
      </p>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
